/* eslint-disable no-param-reassign */
import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { removeTokenCookie, setTokenCookie } from '@root/utils/cookies/cookies';
import * as Sentry from '@sentry/nextjs';
import { CursiveRecorder, isElectron } from 'utils/electron';
import { trackUser } from 'utils/trackUser';

import authService from '../services/authServices';
import emailVerificationService from '../services/emailVerificationService';
import { updateAccountInfo } from './accountSlice';
import { updatedRefreshToken, updatedToken } from './actions/tokenActions';

export const signOut = createAction('signOutGlobally');

export const resetAuthData = createAction('resetAuthData');
export const setErrorMessage = createAction('setErrorMessage');

export const signOutFromServer = createAsyncThunk(
  'auth/signOutFromServer',
  async ({ allDevices } = { allDevices: false }, { rejectWithValue, dispatch }) => {
    const response = await authService
      .signOutFromServer(allDevices)
      .then(() => {
        dispatch(signOut());
        return Promise.resolve();
      })
      .catch((err) => {
        dispatch(signOut());
        return rejectWithValue('Could not register sign out.');
      });

    localStorage.setItem('userSignedOut', 'true');

    return response;
  }
);

export const loginWithEmailAndPassword = createAsyncThunk('auth/emailPassword', async ({ email, password }, { rejectWithValue }) => {
  try {
    return await authService.emailPassword(email, password);
  } catch (err) {
    if (err.status === 401) {
      return rejectWithValue('Invalid email or password');
    }
    if (err.status === 403) {
      return rejectWithValue(err.detail);
    }
    if (err.status === 400) {
      return rejectWithValue(err[0]);
    }

    // Only capture exceptions that do not have a status code or if status code is not handled
    if (!err.status) {
      Sentry.captureException(err);
    }

    return rejectWithValue('Could not sign in.');
  }
});

export const loginWithEmailAndOtp = createAsyncThunk('auth/emailOtp', async ({ email, otp }, { rejectWithValue }) => {
  return authService.emailOtp(email, otp).catch((err) => {
    if (err.status === 401) {
      return rejectWithValue('Invalid email or code.');
    }

    if (!err.status) {
      // If there's no status code, the error is from our web app. In
      // this case, we still want to capture the error but fail
      // gracefully.
      Sentry.captureException(err);
    }

    return rejectWithValue('Could not sign in.');
  });
});

export const loginWithGoogle = createAsyncThunk('auth/google', async ({ authCode, redirectURL }, { rejectWithValue }) => {
  try {
    const userInfo = await authService.google(authCode, redirectURL);

    return userInfo;
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const loginWithGoogleOneTap = createAsyncThunk('auth/googleOneTap', async ({ credential }, { rejectWithValue }) => {
  try {
    const userInfo = await authService.googleOneTap(credential);

    return userInfo;
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const loginWithGooglePopup = createAsyncThunk('auth/googlePopup', async ({ credential }, { rejectWithValue }) => {
  try {
    const userInfo = await authService.googlePopup(credential);

    return userInfo;
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const signUp = createAsyncThunk(
  'auth/signUp',
  (
    { email, firstName = '', lastName = '', password, confirmPassword, save, signupSource, cloudflareToken, fsUUID, inviteCode },
    { rejectWithValue }
  ) => {
    return authService
      .signUp(email, firstName, lastName, password, confirmPassword, '', save, signupSource, false, cloudflareToken, fsUUID, inviteCode)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        Sentry.captureException(err);
        return rejectWithValue(err);
      });
  }
);

export const setToken = createAsyncThunk('auth/setToken', ({ token, refresh }) => {
  setTokenCookie({ access: token, refresh });
  // Handle login and logout for other tabs on our domain. Handled in _app.js.
  // This is not fired for page making the change, so it wont interfere with current tab.
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
  localStorage.removeItem('logoutEvent');
  localStorage.setItem('loginEvent', 'true');
  return Promise.resolve({ token, refresh });
});

export const verifyEmail = createAsyncThunk('auth/verifyEmail', async ({ code, user_id }, { rejectWithValue }) => {
  try {
    const response = await emailVerificationService.verifyEmail(code, user_id);

    setTokenCookie({ access: response.token });
    return response;
  } catch (err) {
    return rejectWithValue(err);
  }
});
export const isDomainSSOEnabled = createAsyncThunk('auth/isDomainSSOEnabled', async ({ domain, email }) => {
  const ssoEnabled = authService.isDomainSSOEnabled({ domain, email });

  return ssoEnabled;
});

export const clearAuthError = createAction('auth/clearAuthError');
export const setAuthError = createAction('auth/setAuthError');

export const INITIAL_STATE = {
  jwtToken: null,
  data: null,
  authenticatedRedirectUrl: null,
  isLoading: false,
  isError: false,
  errorMessage: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: { ...INITIAL_STATE },
  reducers: {
    setAuthenticatedRedirectUrl: (state, action) => {
      state.authenticatedRedirectUrl = action.payload.url;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithEmailAndPassword.fulfilled, (state, action) => {
        state.jwtToken = action.payload.token;
        state.jwtRefreshToken = action.payload.refresh;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(loginWithEmailAndPassword.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(loginWithEmailAndPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(loginWithEmailAndOtp.fulfilled, (state, action) => {
        state.jwtToken = action.payload.token;
        state.jwtRefreshToken = action.payload.refresh;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(loginWithEmailAndOtp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(loginWithEmailAndOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.jwtToken = action.payload.token;
        state.jwtRefreshToken = action.payload.refresh;
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateAccountInfo.fulfilled, (state, action) => {
        const { token, refresh } = action.payload || {};

        if (!!token && !!refresh) {
          state.jwtToken = token;
          state.jwtRefreshToken = refresh;
        }
      })
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload.detail;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.jwtToken = action.payload.token;
        state.jwtRefreshToken = action.payload.refresh;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        const { payload } = action;

        if (typeof payload === 'object' && payload !== null) {
          state.errorMessage = payload[0];
        } else {
          state.errorMessage = payload;
        }
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(signUp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(setToken.fulfilled, (state, action) => {
        if (action.payload.token) {
          state.jwtToken = action.payload.token;
        }
        if (action.payload.refresh) {
          state.jwtRefreshToken = action.payload.refresh;
        }
      })
      .addCase(setToken.pending, (state) => {})
      .addCase(setToken.rejected, (state, action) => {
        state.jwtToken = null;
        state.jwtRefreshToken = null;
      })
      .addCase(isDomainSSOEnabled.fulfilled, (state, action) => {
        state.data = {
          ...state.data,
          ssoEnabledDomain: action.payload.is_sso_enabled_domain
        };
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(isDomainSSOEnabled.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(isDomainSSOEnabled.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(signOutFromServer.fulfilled, (state, action) => {
        if (action.payload?.signout_url) {
          if (isElectron()) {
            CursiveRecorder().openURLInBrowser(action.payload.signout_url);
          } else {
            window.open(action.payload.signout_url);
          }
        }
      })
      .addCase(signOut, () => {
        // Handle login and logout for other tabs on our domain. Handled in _app.js.
        // This is not fired for page making the change, so it wont interfere with current tab.
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
        window.localStorage.setItem('logoutEvent', 'true');
        window.localStorage.removeItem('loginEvent');
        removeTokenCookie();
        return { ...INITIAL_STATE };
      })
      .addCase(resetAuthData, (state, action) => {
        state.data = null;
      })
      .addCase(setErrorMessage, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(clearAuthError, (state) => {
        state.errorMessage = undefined;
      })
      .addCase(setAuthError, (state, action) => {
        state.errorMessage = action.payload;
      })
      .addCase(updatedToken, (state, action) => {
        const newToken = action.payload;

        if (newToken !== state.jwtToken) {
          state.jwtToken = newToken;
        }
      })
      .addCase(updatedRefreshToken, (state, action) => {
        const newRefreshToken = action.payload;

        if (newRefreshToken !== state.jwtRefreshToken) {
          state.jwtRefreshToken = newRefreshToken;
        }
      })
      .addCase(loginWithGoogleOneTap.fulfilled, (state, action) => {
        if (action.payload?.is_signup) {
          state.data = action.payload;
          trackUser(action.payload);
        }
      });
  }
});

export const selectAuthWithEmail = createSelector(
  [
    (state) => state.auth.jwtToken,
    (state) => state.auth.isError,
    (state) => state.auth.isLoading,
    (state) => state.auth.errorMessage,
    (state) => state.auth.authenticatedRedirectUrl
  ],
  (jwtToken, isError, isLoading, errorMessage, authenticatedByEmailRedirectUrl) => ({
    jwtToken,
    isError,
    isLoading,
    errorMessage,
    authenticatedByEmailRedirectUrl
  })
);

export const selectVerifyEmail = createSelector(
  [(state) => state.auth.errorMessage, (state) => state.auth.isLoading, (state) => state.auth.isError],
  (errorMessage, isLoading, isError) => ({ errorMessage, isLoading, isError })
);

export const selectTokenPair = createSelector(
  [(state) => state.auth.jwtToken, (state) => state.auth.jwtRefreshToken],
  (token, refresh) => ({ token, refresh })
);

export default authSlice.reducer;
